import * as React from 'react'
import useRouter from 'use-react-router'
import styled from 'styled-components'
import {
  faEllipsisV,
  faTrashAlt,
  faEnvelopeOpen
} from '@fortawesome/pro-light-svg-icons'

import { EuroCents, toCurrency } from '@espoon-voltti/ts-libs/lib/euro'

import { Table, Button } from '@espoon-voltti/react-components/elements'
import { StatusType } from '@espoon-voltti/react-components/types/common'
import { ContextMenu } from '@espoon-voltti/react-voltti/components/context-menu'

import { GrantPeriod } from '@espoon-voltti/nuora-domain/grants'

import {
  ApprovedPreReportDecision,
  GrantApp,
  RejectedPostReportDecision,
  PayImmediatelyWaitReportDecision,
  PayAfterReportDecision,
  PaymentWaitApp
} from '../../domain'

import { t } from '../../i18n'
import { localizedPathnames } from '../../utils/localized-pathname'

import { sortApps } from './app-list-sort'

const AppListWrapper = styled.div`
  margin-left: -40px;
  margin-right: -40px;
  margin-bottom: 80px;
  position: relative;
`

const Th = styled(Table.Th)`
  ${(p: { width?: string }) => p.width && `width: ${p.width};`}
`

const getDisplayName = (x: GrantApp) => {
  switch (x.form.type) {
    case 'PROJECT_GRANT':
      return x.form.event.name
    case 'GENERAL_GRANT':
    case 'GENERAL_GRANT_PAPER':
      return t(['views', 'generalGrant', 'form', 'title'])
    default:
      return ''
  }
}

interface AppListProps {
  apps: GrantApp[]
  grantPeriods: GrantPeriod[]
  onDelete: (appId: string) => unknown
}
export const AppList: React.FC<AppListProps> = (p) => {
  const router = useRouter()

  const handleClick = (app: GrantApp) => () =>
    router.history.push(localizedPathnames.application(app.id))

  const handleDelete = (app: GrantApp) => () => p.onDelete(app.id)

  const sortedApps = sortApps(p.apps)

  return (
    <AppListWrapper>
      <Table.Table interactive={true} className={'has-fixed-layout'}>
        <Table.Head>
          <Table.Row>
            <Th width={'20%'}>
              {t(['views', 'home', 'org', 'apps', 'columns', 'grantPeriod'])}
            </Th>
            <Table.Th>
              {t(['views', 'home', 'org', 'apps', 'columns', 'eventName'])}
            </Table.Th>
            <Th width={'10%'}>
              {t(['views', 'home', 'org', 'apps', 'columns', 'appliedSum'])}
            </Th>
            <Th width={'10%'}>
              {t(['views', 'home', 'org', 'apps', 'columns', 'decisionSum'])}
            </Th>
            <Table.Th>
              {t(['views', 'home', 'org', 'apps', 'columns', 'status'])}
            </Table.Th>
            <Th width={'5%'} />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {sortedApps.map((app) => (
            <Table.Row
              key={app.id}
              dataQa={`app-${app.id}`}
              onClick={handleClick(app)}
              status={rowStatusType(app)}
            >
              <Table.Td dataQa="grantPeriod">
                {p.grantPeriods.find((x) => x.id === app.grantPeriodId)?.name ??
                  ''}
              </Table.Td>
              <Table.Td dataQa="eventName">{getDisplayName(app)}</Table.Td>
              <Table.Td dataQa="appliedSum" className={'has-no-wrap-text'}>
                {toCurrency(app.form.appliedSum)}
              </Table.Td>
              <Table.Td dataQa="decidedSum" className={'has-no-wrap-text'}>
                {decidedSum(app)}
              </Table.Td>
              <Table.Td>
                <div data-qa="status" className="is-uppercase">
                  <strong>{t(['common', 'statuses', app.state])}</strong>
                </div>
                <div data-qa="statusDetails">{statusDetails(app)}</div>
              </Table.Td>
              <Table.Td align="right">
                <ContextMenu.Container>
                  <ContextMenu.Trigger dataQa="contextTrigger">
                    <Button
                      dark
                      plain
                      width={'narrow'}
                      icon={faEllipsisV}
                      iconSize="2x"
                    />
                  </ContextMenu.Trigger>
                  <ContextMenu.Items>
                    <ContextMenu.Item
                      dataQa="openApplication"
                      icon={faEnvelopeOpen}
                      onClick={handleClick(app)}
                    >
                      {t(['views', 'home', 'org', 'apps', 'actions', 'open'])}
                    </ContextMenu.Item>
                    <ContextMenu.Item
                      dataQa="deleteApplication"
                      icon={faTrashAlt}
                      onClick={handleDelete(app)}
                      disabled={
                        app.state !== 'DRAFT' && app.state !== 'SIGNING'
                      }
                    >
                      {t(['views', 'home', 'org', 'apps', 'actions', 'delete'])}
                    </ContextMenu.Item>
                  </ContextMenu.Items>
                </ContextMenu.Container>
              </Table.Td>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Table>
    </AppListWrapper>
  )
}

const rowStatusType = (app: GrantApp): StatusType | undefined => {
  switch (app.state) {
    case 'DRAFT':
      return 'new'
    case 'SIGNING':
    case 'RECOVERY':
      return 'alert'
    case 'REVIEW':
      return 'visited'
    case 'SENT':
    case 'PAYMENT_WAIT':
    case 'PRE_REPORT':
    case 'POST_REPORT':
    case 'REJECTED':
    case 'PAID':
    case 'RECOVERED':
      return undefined
    default:
      const _: never = app
      return _
  }
}

const decidedSum = (app: GrantApp): string => {
  switch (app.state) {
    case 'DRAFT':
    case 'SIGNING':
    case 'SENT':
    case 'REVIEW':
      return ''
    case 'PAYMENT_WAIT':
    case 'PRE_REPORT':
    case 'POST_REPORT':
    case 'PAID':
    case 'RECOVERY':
    case 'RECOVERED':
      return toCurrency(app.decision.amountInCents)
    case 'REJECTED':
      return toCurrency(EuroCents.zero())
    default:
      const _: never = app
      return _
  }
}

const statusDetails = (app: GrantApp): string => {
  switch (app.state) {
    case 'DRAFT':
      return app.modifiedAt.toLocalDate().format()
    case 'SENT':
      return app.sent.sentAt.toLocalDate().format()
    case 'SIGNING':
    case 'REVIEW':
      return ''
    case 'PRE_REPORT':
      return preReportWait(app.decision)
    case 'REJECTED':
      return app.preReportDecision == null
        ? ''
        : t(['views', 'home', 'org', 'apps', 'statusDesc', 'preReportRejected'])
    case 'PAYMENT_WAIT':
      return paymentWaitDesc(app)
    case 'POST_REPORT':
      return postReportPending(app.decision)
    case 'PAID':
      return app.postReportDecision == null
        ? ''
        : t([
            'views',
            'home',
            'org',
            'apps',
            'statusDesc',
            'postReportApproved'
          ])
    case 'RECOVERY':
      return recovery(app.postReportDecision)
    case 'RECOVERED':
      return recovered(app.postReportDecision)
    default:
      const _: never = app
      return _
  }
}

const preReportWait = (decision: PayAfterReportDecision) =>
  t(['views', 'home', 'org', 'apps', 'statusDesc', 'preReportWait'], {
    dueDate: decision.reportDeadlineDate.format('fi')
  })

const paymentWaitDesc = (app: PaymentWaitApp): string => {
  const decision = app.decision
  switch (decision.decision) {
    case 'GRANTED_PAY_AFTER_REPORT':
      // pre report decision must exist with this decision type in payment wait state,
      // otherwise app would have been rejected
      return approvedPreReport(app.preReportDecision!)
    case 'GRANTED_PAY_IMMEDIATELY_WAIT_REPORT':
      return t(
        ['views', 'home', 'org', 'apps', 'statusDesc', 'postReportPaymentWait'],
        { dueDate: decision.reportDeadlineDate.format('fi') }
      )
    case 'GRANTED_PAY_IMMEDIATELY':
      return t(['views', 'home', 'org', 'apps', 'statusDesc', 'payImmediately'])
  }
}

const postReportPending = (decision: PayImmediatelyWaitReportDecision) =>
  t(['views', 'home', 'org', 'apps', 'statusDesc', 'postReportPending'], {
    dueDate: decision.reportDeadlineDate.format('fi')
  })

const approvedPreReport = (decision: ApprovedPreReportDecision) =>
  t(['views', 'home', 'org', 'apps', 'statusDesc', 'preReportApproved'], {
    amount: decision.amountInCents.euros()
  })

const recovery = (decision: RejectedPostReportDecision) =>
  t(['views', 'home', 'org', 'apps', 'statusDesc', 'recovery'], {
    amount: decision.amountInCents.euros(),
    dueDate: decision.dueDate.format('fi')
  })

const recovered = (decision: RejectedPostReportDecision) =>
  t(['views', 'home', 'org', 'apps', 'statusDesc', 'recovered'], {
    amount: decision.amountInCents.euros()
  })
